import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, Trash2, Phone, Mail } from "lucide-react";
import { api, fmtErr, getToken, API } from "./api";

export const InquiriesTab = () => {
    const [rows, setRows] = useState([]);

    const load = () =>
        api().get("/admin/inquiries").then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e)));
    useEffect(() => {
        load();
    }, []);

    const download = async () => {
        try {
            const res = await fetch(`${API}/admin/inquiries/export`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "zzf-inquiries.csv";
            a.click();
            URL.revokeObjectURL(a.href);
            toast.success("Excel file downloaded");
        } catch (e) {
            toast.error("Download failed. Please try again.");
        }
    };

    const remove = async (r) => {
        if (!window.confirm(`Delete inquiry from ${r.name}?`)) return;
        try {
            await api().delete(`/admin/inquiries/${r.id}`);
            toast.success("Inquiry deleted");
            load();
        } catch (e) {
            toast.error(fmtErr(e));
        }
    };

    return (
        <div data-testid="inquiries-tab">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-heading font-bold text-2xl text-navy tracking-tight">Customer Inquiries</h2>
                    <p className="text-sm text-muted mt-1">
                        {rows.length} inquir{rows.length === 1 ? "y" : "ies"} received from your website's quote form.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} className="inline-flex items-center gap-2 border border-line px-5 py-4 font-semibold text-charcoal hover:border-navy transition-colors" data-testid="refresh-inquiries-button">
                        <RefreshCw size={17} /> Refresh
                    </button>
                    <button onClick={download}
                        className="inline-flex items-center gap-2 bg-navy text-white px-6 py-4 font-semibold hover:bg-gold transition-colors"
                        data-testid="export-inquiries-button">
                        <Download size={17} /> Download Excel (CSV)
                    </button>
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-line" data-testid="inquiries-empty">
                    <p className="text-lg font-semibold text-navy">No inquiries yet</p>
                    <p className="text-sm text-muted mt-1">When a customer fills the "Get a Quote" form on your website, it appears here.</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px] text-left" data-testid="inquiries-table">
                        <thead>
                            <tr className="border-b border-line text-xs uppercase tracking-wider text-muted">
                                <th className="py-3 pr-4">Date</th>
                                <th className="py-3 pr-4">Customer</th>
                                <th className="py-3 pr-4">Contact</th>
                                <th className="py-3 pr-4">Interested In</th>
                                <th className="py-3 pr-4">Message</th>
                                <th className="py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-line align-top" data-testid={`inquiry-row-${r.id}`}>
                                    <td className="py-4 pr-4 text-xs text-muted whitespace-nowrap">
                                        {r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                                    </td>
                                    <td className="py-4 pr-4">
                                        <p className="font-semibold text-navy">{r.name}</p>
                                        {r.business_name && <p className="text-xs text-muted">{r.business_name}</p>}
                                    </td>
                                    <td className="py-4 pr-4 text-sm">
                                        <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 text-navy hover:text-gold" data-testid={`inquiry-phone-${r.id}`}>
                                            <Phone size={13} /> {r.phone}
                                        </a>
                                        <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 text-muted hover:text-gold mt-1" data-testid={`inquiry-email-${r.id}`}>
                                            <Mail size={13} /> {r.email}
                                        </a>
                                    </td>
                                    <td className="py-4 pr-4">
                                        <span className="text-xs font-semibold bg-bone border border-line px-2.5 py-1">{r.requirement_type}</span>
                                    </td>
                                    <td className="py-4 pr-4 text-sm text-muted max-w-[260px]">{r.message || "—"}</td>
                                    <td className="py-4">
                                        <button onClick={() => remove(r)} className="p-2.5 border border-line hover:border-red-500 hover:text-red-600" data-testid={`delete-inquiry-${r.id}`} aria-label="Delete">
                                            <Trash2 size={15} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
